import { Button } from '@talentmesh/ui/components/button';
import { Empty, EmptyDescription } from '@talentmesh/ui/components/empty';
import { RadioGroup } from '@talentmesh/ui/components/radio-group';
import { Spinner } from '@talentmesh/ui/components/spinner';
import { AtsJobResponse } from '../../api/useAtsJobs';
import AtsDialogLoading from './AtsDialogLoading';
import AtsJobOption from './AtsJobOption';
import UIStrings from '@/Utils/UIStrings';

interface AtsJobListContentProps {
    jobs: AtsJobResponse[];
    selectedJob: AtsJobResponse | null;
    onSelectedJobChange: (job: AtsJobResponse) => void;
    isLoading: boolean;
    isError: boolean;
    hasFilters: boolean;
    hasMore: boolean;
    isLoadingMore: boolean;
    onLoadMore: () => void;
}

/** Picks which state the list body is in: loading, failed, empty, or the selectable jobs. */
const AtsJobListContent = ({
    jobs,
    selectedJob,
    onSelectedJobChange,
    isLoading,
    isError,
    hasFilters,
    hasMore,
    isLoadingMore,
    onLoadMore,
}: AtsJobListContentProps) => {
    if (isLoading) {
        return <AtsDialogLoading />;
    }

    if (isError) {
        return (
            <Empty className="py-8" data-cy="AtsJobPickerError">
                <div>
                    <EmptyDescription>{UIStrings.AtsJobsLoadFailed}</EmptyDescription>
                    <EmptyDescription>{UIStrings.AtsJobsLoadFailedRetryHint}</EmptyDescription>
                </div>
            </Empty>
        );
    }

    if (jobs.length === 0) {
        return (
            <Empty className="py-8" data-cy="AtsJobPickerEmpty">
                <EmptyDescription>
                    {hasFilters ? UIStrings.NoAtsJobsFoundDescription : UIStrings.NoAtsJobsAtAll}
                </EmptyDescription>
            </Empty>
        );
    }

    return (
        <>
            <RadioGroup
                className="my-auto gap-2"
                value={selectedJob?.id ?? ''}
                onValueChange={(jobId) => {
                    const job = jobs.find((candidate) => candidate.id === jobId);

                    if (job) {
                        onSelectedJobChange(job);
                    }
                }}
                aria-label={UIStrings.SelectAtsJobTitle}
            >
                {jobs.map((job) => (
                    <AtsJobOption key={job.id} job={job} />
                ))}
            </RadioGroup>
            {hasMore && (
                <div className="flex shrink-0 justify-center pt-4">
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-primary"
                        disabled={isLoadingMore}
                        onClick={onLoadMore}
                        data-cy="AtsJobPickerLoadMore"
                    >
                        {isLoadingMore && <Spinner className="size-4" />}
                        {UIStrings.LoadMoreAtsJobs}
                    </Button>
                </div>
            )}
        </>
    );
};

interface AtsJobListProps {
    jobs: AtsJobResponse[];
    selectedJob: AtsJobResponse | null;
    onSelectedJobChange: (job: AtsJobResponse) => void;
    /** True on the very first fetch, before any page has arrived. */
    isLoading: boolean;
    /** True when the connection is fine but the jobs request failed. */
    isError: boolean;
    /** Whether a search or non-'All' status is active, so the empty state can say why. */
    hasFilters: boolean;
    hasMore: boolean;
    /** True while an additional page is being fetched. */
    isLoadingMore: boolean;
    onLoadMore: () => void;
}

const AtsJobList = ({
    jobs,
    selectedJob,
    onSelectedJobChange,
    isLoading,
    isError,
    hasFilters,
    hasMore,
    isLoadingMore,
    onLoadMore,
}: AtsJobListProps) => (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" data-cy="AtsJobPickerList">
        <AtsJobListContent
            jobs={jobs}
            selectedJob={selectedJob}
            onSelectedJobChange={onSelectedJobChange}
            isLoading={isLoading}
            isError={isError}
            hasFilters={hasFilters}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            onLoadMore={onLoadMore}
        />
    </div>
);

export default AtsJobList;
